/**
 * First-visit section nudge resolution (EPIC #1321, part 8/9; REQ D4-ONB-010).
 *
 * The nudge layer (SectionNudge.tsx) is GENERIC over the central tour registry:
 * the first time a user opens a section that has a registered mini-tour they
 * have not completed/dismissed, a small dismissible toast offers that tour. New
 * section tours -- the agent set (4/9, #1325) and the admin set (#1326, landing
 * concurrently) -- get a nudge for FREE simply by being registered; there is no
 * per-page hand-wiring and this file never edits the registry (it only READS it).
 *
 * What counts as a "section" tour: every registered tour EXCEPT the persona
 * welcome tours (3/9 -- those fire once from the first-login welcome prompt on
 * the landing page, never as a section nudge) and the ``crm-orientation`` demo.
 * A section tour's START ROUTE is its first step's ``route``; a nudge is offered
 * when the current pathname matches that start route exactly.
 *
 * Copy rules (D4-ONB-011 / D4-WLB): agency-neutral via the ``{agency}`` token,
 * no emojis, plain professional language for non-technical realtors.
 */
import { TOUR_REGISTRY } from "./registry";
import { WELCOME_TOURS } from "./welcomeTours";
import { CRM_ORIENTATION_TOUR } from "./registry";
import { ADMIN_PAGE_TOURS, OVERSIGHT_PAGE_TOURS } from "./adminPageTours";
import { AGENT_PAGE_TOURS } from "./agentPageTours";
import { TEST_DEAL_TOUR } from "./testDealTour";
import { PORTAL_PANEL_TOUR } from "./portalPanelTour";
import type { TourDefinition } from "./types";

/**
 * Tour ids that are NEVER surfaced as a first-visit section nudge:
 *  - the eight persona welcome tours (offered by the welcome prompt on landing);
 *  - the ``crm-orientation`` engine demo (deep-link / visual-verification only);
 *  - the per-PAGE admin tours (#1955) AND the per-PAGE agent/back-office tours
 *    (#1956) -- these are LAUNCHER-ONLY (the header GraduationCap on the route),
 *    deliberately NOT first-visit nudges. A page can carry a launcher tour
 *    without nagging the user with a toast on every first visit; the broader
 *    agent + admin SECTION tours remain the nudge layer.
 *  - the standalone ``test-deal`` "Practice safely" tour (#2195) -- a
 *    LAUNCHER-ONLY walkthrough, never a first-visit nudge.
 */
const NON_NUDGE_TOUR_IDS: ReadonlySet<string> = new Set<string>([
  CRM_ORIENTATION_TOUR.id,
  ...WELCOME_TOURS.map((t) => t.id),
  ...ADMIN_PAGE_TOURS.map((t) => t.id),
  // The manager read-only OVERSIGHT page-tour variants (#2452) are LAUNCHER-ONLY
  // (the header GraduationCap on the /crm/oversight/* mount) -- never a
  // first-visit nudge, exactly like the admin page tours they mirror. The grouped
  // Oversight welcome step covers the section for the manager.
  ...OVERSIGHT_PAGE_TOURS.map((t) => t.id),
  ...AGENT_PAGE_TOURS.map((t) => t.id),
  // The "Practice safely with a test deal" tour (#2195) is LAUNCHER-ONLY
  // (Training + ?tour=test-deal). It deliberately does NOT nudge on /crm/deals
  // -- the agent-deals section tour owns that first-visit slot.
  TEST_DEAL_TOUR.id,
  // The staff "Client portal" tour (D9 WP24, #3551) is LAUNCHER-ONLY too
  // (Training + ?tour=portal-deal-panel): /crm/deals keeps its agent-deals nudge.
  PORTAL_PANEL_TOUR.id,
]);

/** True when a tour is eligible to drive a first-visit section nudge. */
export function isNudgeableTour(tour: TourDefinition): boolean {
  return !NON_NUDGE_TOUR_IDS.has(tour.id) && tour.steps.length > 0;
}

/** The start route of a section tour (its first step's route). */
export function tourStartRoute(tour: TourDefinition): string | null {
  return tour.steps[0]?.route ?? null;
}

/** Every nudgeable section tour in the registry (welcome tours + demo excluded). */
export function nudgeableSectionTours(): TourDefinition[] {
  return Object.values(TOUR_REGISTRY).filter(isNudgeableTour);
}

/**
 * The section tour whose START ROUTE matches ``pathname`` exactly, or null.
 *
 * Registry-driven and generic: a section gets a nudge purely by registering a
 * tour whose first step lives on that route. If two nudgeable tours ever shared
 * a start route the first in registry order wins (deterministic); in practice
 * each section route maps to one mini-tour.
 */
export function sectionTourForRoute(
  pathname: string,
): TourDefinition | undefined {
  return nudgeableSectionTours().find(
    (tour) => tourStartRoute(tour) === pathname,
  );
}

/**
 * A short, human section label for the nudge headline ("First time in <label>?").
 *
 * Derived from the tour TITLE with a trailing " tour" stripped (e.g.
 * "Listings tour" -> "Listings", "Comms and Docs tour" -> "Comms and Docs"),
 * so a newly registered section tour produces a sensible nudge label with no
 * extra wiring. Falls back to the full title if it does not end in " tour".
 */
export function sectionNudgeLabel(tour: TourDefinition): string {
  const title = tour.title.trim();
  return title.replace(/\s+tour$/i, "").trim() || title;
}
